"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { isGlassHeader } from "@/lib/site";
import { useGameBrowse } from "@/lib/api/hooks";
import type { NavItem } from "@/lib/content/types";
import { Icon } from "@/components/ui/Icon";
import { useContent } from "./ContentContext";
import { RemoteImage } from "@/components/ui/RemoteImage";

const CLOSE_DELAY_MS = 120;
const GAME_LIMIT = 30;

type Row = { key: string; label: string; href: string; icon: NavItem["icon"]; imageUrl?: string };

/* One link in the Topbar layout's menu, and its drop-down when the link asks
   for one (Navigation → Primary → Drop-down): "Casino categories" lists the
   platform's categories, "Games" the games of the category the link opens.
   It opens on hover and on keyboard focus and closes on leaving or Escape;
   the link itself still navigates. The panel is placed from the link's
   position on screen, because the menu row clips what overflows it. */
export function TopbarMenuLink({
  item,
  active,
  casinoNav,
}: {
  item: NavItem;
  active: boolean;
  casinoNav: NavItem[];
}) {
  const glass = isGlassHeader(useContent().flags.headerStyle);
  const menu = item.menu === "Casino categories" || item.menu === "Games" ? item.menu : null;
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (!menu) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const rect = wrapper.current?.getBoundingClientRect();
    if (rect) setAnchor({ top: rect.bottom + 6, left: rect.left });
    setOpen(true);
  };
  const hide = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  /* The games come from the category this link opens (`/c/<slug>`), looked up
     among the platform's categories for its id, and are read only once the
     panel has been opened. */
  const categoryId =
    menu === "Games" ? (casinoNav.find((category) => category.href === item.href)?.categoryId ?? null) : null;
  const games = useGameBrowse({ categoryId, limit: GAME_LIMIT, enabled: open && Boolean(categoryId) });

  const rows: Row[] =
    menu === "Casino categories"
      ? casinoNav.map((category) => ({
          key: category.href,
          label: category.label,
          href: category.href,
          icon: category.icon,
          imageUrl: category.iconUrl,
        }))
      : menu === "Games"
        ? games.games.map((game) => ({
            key: game.id,
            label: game.title,
            href: `/game/${game.slug ?? game.id}`,
            icon: "gamepad",
            imageUrl: game.thumbnailUrl,
          }))
        : [];

  return (
    <div
      ref={wrapper}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hide();
      }}
      className="shrink-0"
    >
      <Link
        data-pill=""
        href={item.href}
        aria-current={active ? "page" : undefined}
        aria-haspopup={menu ? "menu" : undefined}
        aria-expanded={menu ? open : undefined}
        className={cn(
          "flex h-[40px] items-center gap-[8px] rounded-lg px-[12px] text-[14px] font-bold leading-[1.6] transition-colors duration-200",
          /* On the glass bar every link is a soft pill, the lit one brighter. */
          glass
            ? active || open
              ? "bg-fg/[0.16] text-fg"
              : "bg-fg/[0.06] text-fg hover:bg-fg/[0.12]"
            : active || open
              ? "bg-elevation-2 text-fg"
              : "text-fg-subtle hover:text-fg",
        )}
      >
        <Icon name={item.icon} size={16} />
        {item.label}
      </Link>

      <AnimatePresence>
        {menu && open && anchor && (
          <motion.div
            role="menu"
            aria-label={item.label}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: "fixed", top: anchor.top, left: anchor.left }}
            onMouseEnter={show}
            onMouseLeave={hide}
            className="modal-shadow scrollbar-none z-50 max-h-[70vh] w-[220px] overflow-y-auto rounded-lg border border-border bg-elevation-2 p-[6px]"
          >
            {rows.map((row) => (
              <Link
                key={row.key}
                role="menuitem"
                href={row.href}
                onClick={() => setOpen(false)}
                className="flex h-[38px] items-center gap-[10px] rounded-md px-[10px] text-[13px] font-bold leading-[1.6] text-fg-muted transition-colors duration-150 hover:bg-elevation-3 hover:text-fg focus-visible:bg-elevation-3 focus-visible:text-fg focus-visible:outline-none"
              >
                {row.imageUrl ? (
                  <span className="relative grid h-[18px] w-[18px] shrink-0 place-items-center overflow-hidden rounded-[4px]">
                    <RemoteImage src={row.imageUrl} alt="" width={18} height={18} />
                  </span>
                ) : (
                  <Icon name={row.icon} size={16} className="shrink-0" />
                )}
                <span className="truncate">{row.label}</span>
              </Link>
            ))}
            {rows.length === 0 && (
              <p className="px-[10px] py-[8px] text-[12px] leading-[1.6] text-fg-subtle">
                {menu === "Games" && (games.isPending || games.isFetching) && categoryId ? "Loading…" : "Nothing here yet."}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
